/**
 * Onboarding quick-suggestion actions from /api/chat/quick-suggestions
 */

import {
  getDayPartWindow,
  getDayPartWindowDateKey,
  type DayPartWindow,
} from '@utils/dayPartWindow';
import { PostMessageType } from './postMessage';

export type QuickSuggestionActionType = 'CHATBOT' | 'DEEPLINK' | string;

export interface QuickSuggestion {
  featureKey: string;
  order: number;
  bubbleText?: string;
  description: string;
  imageUrl: string;
  type?: string;
  title: string;
  actionType: QuickSuggestionActionType;
  actionValue: string;
}

export interface QuickSuggestionsData {
  suggestions: QuickSuggestion[];
  maxVisible?: number;
  totalCount?: number;
  isFirstSessionVisitEvent?: boolean;
  isFirstSession?: boolean;
  showSuggestion?: boolean;
  currentSession?: string;
}

/** Parent (seller) listens for `CHATBOT_TIP_BUBBLE` with `payload.tips`. */
export const CHATBOT_TIP_BUBBLE_MESSAGE = PostMessageType.CHATBOT_TIP_BUBBLE;

/** Parent → chatbot iframe when user clicks a tip bubble. */
export const CHATBOT_TIP_ACTION_MESSAGE = PostMessageType.CHATBOT_TIP_ACTION;

export type QuickSuggestionTip = {
  text: string;
  featureKey: string;
  actionType: QuickSuggestionActionType;
  actionValue: string;
  title: string;
  /** false = pin card lên đầu list, true = chạy action. Default true. */
  executeAction?: boolean;
};

const normalizePinText = (value?: string) => (value || '').trim().toLowerCase();

export const pinQuickSuggestionToTop = (
  suggestions: QuickSuggestion[],
  pin: Pick<QuickSuggestionTip, 'featureKey' | 'title' | 'text'> & {
    description?: string;
  },
): QuickSuggestion[] => {
  if (!suggestions.length) return suggestions;

  const pinKey = normalizePinText(pin.featureKey);
  const pinTitle = normalizePinText(pin.title);
  const pinText = normalizePinText(pin.text || pin.description);

  const index = suggestions.findIndex((item) => {
    if (pinKey && normalizePinText(item.featureKey) === pinKey) return true;
    if (pinTitle && normalizePinText(item.title) === pinTitle) return true;
    if (!pinText) return false;
    return (
      normalizePinText(item.bubbleText) === pinText ||
      normalizePinText(item.title) === pinText ||
      normalizePinText(item.description) === pinText
    );
  });

  if (index < 0) return suggestions;
  if (index === 0) return suggestions;

  const next = [...suggestions];
  const [item] = next.splice(index, 1);
  return [item, ...next];
};

const mapSuggestionToTip = (item: QuickSuggestion): QuickSuggestionTip => ({
  text: item.bubbleText?.trim() || '',
  featureKey: item.featureKey || '',
  actionType: item.actionType,
  actionValue: item.actionValue || '',
  title: item.title || '',
});

export const getQuickSuggestionTips = (
  suggestions: QuickSuggestion[],
  maxCount = 3,
): QuickSuggestionTip[] =>
  suggestions
    .slice(0, maxCount)
    .map(mapSuggestionToTip)
    .filter((tip) => Boolean(tip.text));

/**
 * Outside tip bubble: pick up to `maxCount` random indexes in `[0, totalCount)`,
 * sort indexes ascending so display follows list/table order.
 */
export const pickRandomQuickSuggestionTips = (
  suggestions: QuickSuggestion[],
  maxCount = 3,
  totalCount?: number,
): QuickSuggestionTip[] => {
  const size = Math.min(
    suggestions.length,
    typeof totalCount === 'number' && totalCount > 0 ? totalCount : suggestions.length,
  );
  if (size <= 0) return [];

  const pickCount = Math.min(maxCount, size);
  const picked = new Set<number>();
  while (picked.size < pickCount) {
    picked.add(Math.floor(Math.random() * size));
  }

  return [...picked]
    .sort((a, b) => a - b)
    .map((index) => mapSuggestionToTip(suggestions[index]))
    .filter((tip) => Boolean(tip.text));
};

/** @deprecated Use DayPartWindow from `@utils/dayPartWindow`. */
export type TipBubbleTimeWindow = DayPartWindow;

/** @deprecated Use getDayPartWindow from `@utils/dayPartWindow`. */
export const getTipBubbleTimeWindow = getDayPartWindow;

/** @deprecated Use getDayPartWindowDateKey from `@utils/dayPartWindow`. */
export const getTipBubbleWindowDateKey = getDayPartWindowDateKey;

// localStorage flag: tip bubble already sent for this user + shop + day-part window
const TIP_BUBBLE_CONSUME_PREFIX = 'chatbot_tip_bubble_sent';

const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** userId (JWT sub) + businessId — used to scope tip-bubble once-per-window. */
export const getTipBubbleIdentityFromToken = (
  token: string,
): { userId: string; businessId: string } | null => {
  const payload = parseJwtPayload(token);
  if (!payload) return null;

  const businessId = payload.business_id != null ? String(payload.business_id) : '';
  const sub = payload.sub != null ? String(payload.sub) : '';
  const userId = (sub.split('|')[0] || sub).trim();

  if (!userId && !businessId) return null;
  return {
    userId: userId || 'unknown',
    businessId: businessId || 'unknown',
  };
};

/** Storage key: `prefix:user:business:date:morning|afternoon`. */
export const buildTipBubbleConsumeKey = (
  identity: { userId: string; businessId: string },
  now: Date = new Date(),
): string => {
  const window = getDayPartWindow(now);
  const dateKey = getDayPartWindowDateKey(now);
  return `${TIP_BUBBLE_CONSUME_PREFIX}:${identity.userId}:${identity.businessId}:${dateKey}:${window}`;
};

/** True if we already posted CHATBOT_TIP_BUBBLE for this user+shop+window. */
export const hasConsumedTipBubbleForWindow = (
  token: string,
  now: Date = new Date(),
): boolean => {
  const identity = getTipBubbleIdentityFromToken(token);
  if (!identity) return false;
  try {
    return globalThis.localStorage?.getItem(buildTipBubbleConsumeKey(identity, now)) === '1';
  } catch {
    return false;
  }
};

/** Mark tip bubble as sent so we don't spam the same morning/afternoon window again. */
export const markTipBubbleConsumed = (token: string, now: Date = new Date()): void => {
  const identity = getTipBubbleIdentityFromToken(token);
  if (!identity) return;
  try {
    globalThis.localStorage?.setItem(buildTipBubbleConsumeKey(identity, now), '1');
  } catch {
    // ignore quota / private mode
  }
};

/** @deprecated Prefer getQuickSuggestionTips — kept for text-only consumers. */
export const getQuickSuggestionBubbleTexts = (
  suggestions: QuickSuggestion[],
  maxCount = 3,
): string[] =>
  getQuickSuggestionTips(suggestions, maxCount).map((tip) => tip.text);

/**
 * Legacy local map for PROMPT features (kept as fallback when API is empty).
 */
export type OnboardingFeatureAction = { type: 'PROMPT'; prompt: string };

export const ONBOARDING_FEATURE_ACTIONS: Record<string, OnboardingFeatureAction> =
  {
    'Báo cáo doanh thu': {
      type: 'PROMPT',
      prompt: 'Báo cáo doanh thu',
    },
    'Trend thị trường': {
      type: 'PROMPT',
      prompt: '@tendoo Phân tích sản phẩm nên bán',
    },
  };
